import { useAuthentication } from '~/hooks/useAuthentication';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '~/components/Button/Button';
import { Panel } from '~/components/Panel/Panel';
import { Link } from '~/components/Link/Link';
import { useNavigate } from 'react-router';
import { Input } from '~/components/Input';
import { useForm } from 'react-hook-form';
import { msg } from '~/utils/validation';
import { isAxiosError } from 'axios';
import { useState } from 'react';
import { z } from 'zod';

const schema = z.object({
  username: z.string().min(1, { message: msg.required }),
  password: z.string().min(1, { message: msg.required }),
});

type Schema = z.infer<typeof schema>;

export function LoginForm() {
  const { register, handleSubmit, formState } = useForm<Schema>({
    resolver: zodResolver(schema),
  });

  const [failureReason, setFailureReason] = useState<string>();
  const [isLoading, setIsLoading] = useState(false);

  const auth = useAuthentication();
  const navigate = useNavigate();

  async function onSubmit({ username, password }: Schema) {
    setIsLoading(true);

    try {
      await auth.login(username, password);
      navigate('/');
    } catch (ex) {
      if (isAxiosError(ex) && ex.status === 400) {
        setFailureReason('Du har angivit ett ogiltigt användarnamn eller lösenord.');
      } else {
        setFailureReason('Ett oväntat fel har inträffat, testa igen om en stund.');
        throw ex;
      }
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {failureReason && !isLoading && (
        <Panel size="sm" variant="error" rounded style={{ marginBottom: 'var(--size-4)' }}>
          {failureReason}
        </Panel>
      )}

      <Input.Group label="Användarnamn" formState={formState} htmlFor="username">
        <Input.Field type="text" formState={formState} {...register('username')} />
      </Input.Group>

      <Input.Group label="Lösenord" htmlFor="password" formState={formState}>
        <Input.Field type="password" formState={formState} {...register('password')} />
      </Input.Group>

      <Link to="/auth/reset-password/request">Glömt ditt lösenord?</Link>

      <Button.Group align="right">
        <Button type="submit" isLoading={isLoading}>
          Logga in
        </Button>
      </Button.Group>
    </form>
  );
}
